import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import Global from '../store/global-selectors';
import { useFeatureFlag } from '../feature-flags/hooks/use-feature-flag';
import type { DdpCountryConfig } from '../config/ddp-country-config';
import { getDdpCountryConfig, getDdpTermsUrl, DEFAULT_SNOOZE_DURATION_HOURS } from '../config/ddp-country-config';

type UseDdpCountryConfigResult = {
  isDdpAvailable: boolean;
  isFeatureFlagEnabled: boolean;
  isCountryEligible: boolean;
  config: DdpCountryConfig | null;
  carriers: string[];
  walletedCarriers: string[];
  termsLabel: string | null;
  termsUrl: string | null;
  shouldShowIntroductionModal: boolean;
  introductionMessages: DdpCountryConfig['introductionMessages'] | null;
  termsMessages: DdpCountryConfig['termsMessages'] | null;
  snoozeDurationHours: number;
};

/**
 * Fallback flag used when:
 * 1. Country is not in DDP_COUNTRY_CONFIG (non-eligible country)
 * 2. Country config exists but featureFlag is omitted (feature flag removed after rollout)
 *
 * In both cases, we return true (always enabled) because:
 * - For non-eligible countries: isCountryEligible will be false, so isDdpAvailable = false
 * - For eligible countries without flag: DDP should be always on (flag cleanup scenario)
 */
const ALWAYS_ENABLED_FLAG = {
  key: '__ddp_always_enabled__',
  defaultValue: true,
};

/**
 * Hook to get DDP configuration for the current seller's country.
 *
 * This hook:
 * 1. Gets the seller's home country from Redux
 * 2. Looks up the country-specific DDP configuration
 * 3. Checks the country-specific feature flag
 * 4. Returns combined availability and configuration data
 *
 * Feature Flags (per-country):
 * - US: 'enable-prepaid-duties-and-tax-us'
 * - CA: 'enable-prepaid-duties-and-tax-ca'
 */
export const useDdpCountryConfig = (): UseDdpCountryConfigResult => {
  const sellerHomeCountry = useSelector(Global.selectSellerHomeCountry) as string | null | undefined;

  // Get country-specific configuration
  const countryConfig = useMemo(() => {
    return getDdpCountryConfig(sellerHomeCountry);
  }, [sellerHomeCountry]);

  // Check if country is DDP-eligible (has config entry)
  const isCountryEligible = countryConfig !== null;

  // Get the feature flag declaration for this country
  // Falls back to ALWAYS_ENABLED_FLAG when:
  // - Country not in config (isCountryEligible=false will prevent DDP)
  // - Country in config but featureFlag omitted (DDP always on after flag cleanup)
  const featureFlag = countryConfig?.featureFlag ?? ALWAYS_ENABLED_FLAG;

  // Check the country-specific feature flag using the declaration from feature-flag-declarations.js
  const isFeatureFlagEnabled = useFeatureFlag(featureFlag);

  // DDP is available when:
  // 1. Country is in DDP_COUNTRY_CONFIG (isCountryEligible)
  // 2. Feature flag is enabled OR flag was removed (isFeatureFlagEnabled)
  const isDdpAvailable = isCountryEligible && isFeatureFlagEnabled;

  // Generate terms URL from termsLabel
  const termsUrl = useMemo(() => {
    return getDdpTermsUrl(sellerHomeCountry);
  }, [sellerHomeCountry]);

  return {
    // Availability flags
    isDdpAvailable,
    isFeatureFlagEnabled,
    isCountryEligible,

    // Full configuration object (null if country not supported)
    config: countryConfig,

    // Convenience accessors
    carriers: countryConfig?.carriers ?? [],
    walletedCarriers: countryConfig?.walletedCarriers ?? [],
    termsLabel: countryConfig?.termsLabel ?? null,
    termsUrl,
    shouldShowIntroductionModal: countryConfig?.shouldShowIntroductionModal ?? false,
    introductionMessages: countryConfig?.introductionMessages ?? null,
    termsMessages: countryConfig?.termsMessages ?? null,
    snoozeDurationHours: countryConfig?.snoozeDurationHours ?? DEFAULT_SNOOZE_DURATION_HOURS,
  };
};
